import type { BadgeProps } from '@/components/ui/Badge'
import type {
  InvoiceStatus,
  InvoiceOperation,
  InvoiceType,
  InvoiceRowFragment,
  InvoiceDetailFragment,
} from '@/gql/graphql'
import type { Persona } from '@/lib/session/session.types'
import { InvoiceAclKeys } from '@/lib/acl/keys/invoice.acl_keys'
import { formatCurrency } from '@/lib/formatters'

/** Anything with the fields the money/status helpers need — row or detail fragment. */
export type InvoiceLike = Pick<
  InvoiceRowFragment,
  'status' | 'currency' | 'amount' | 'billingAmount' | 'raisedByParty' | 'raisedByTenant' | 'supplierPartyId'
>

// ── Status metadata ────────────────────────────────────────────────
// All 25 lifecycle statuses. `displayStatus` from the server is the preferred label when present —
// this table is the fallback and, more importantly, the source of the BADGE VARIANT, which the
// server does not send.

type StatusMeta = { label: string; variant: NonNullable<BadgeProps['variant']> }

export const STATUS_META: Record<InvoiceStatus, StatusMeta> = {
  // authoring / raising
  DRAFT: { label: 'Draft', variant: 'neutral' },
  READY_TO_RAISE: { label: 'Ready to raise', variant: 'neutral' },
  SUBMITTED: { label: 'Raised', variant: 'info' },
  MANAGER_RAISED: { label: 'Raised by manager', variant: 'info' },
  MANAGER_APPROVED: { label: 'Manager approved', variant: 'warning' },
  MANAGER_REJECTED: { label: 'Manager rejected', variant: 'danger' },

  // issued / receiver processing
  ISSUED: { label: 'Issued', variant: 'info' },
  RECEIVED: { label: 'Received', variant: 'info' },
  PENDING: { label: 'Pending', variant: 'neutral' },
  PROCESSING: { label: 'Processing', variant: 'info' },
  APPROVED: { label: 'Approved', variant: 'warning' },
  VERIFIED: { label: 'Verified', variant: 'warning' },
  REJECTED: { label: 'Rejected', variant: 'danger' },

  // payment / settlement
  PAYOUT_SCHEDULED: { label: 'Payout scheduled', variant: 'warning' },
  PAYMENT_INITIATED: { label: 'Payment initiated', variant: 'warning' },
  PAYMENT_RECEIVED: { label: 'Payment received', variant: 'success' },
  PAYMENT_FAILED: { label: 'Payment failed', variant: 'danger' },
  PARTIALLY_PAID: { label: 'Partially paid', variant: 'warning' },
  PAID: { label: 'Paid', variant: 'success' },
  OVERDUE: { label: 'Overdue', variant: 'danger' },

  // recall / expiry / hold
  HOLD: { label: 'On hold', variant: 'warning' },
  RECALLED: { label: 'Recalled', variant: 'neutral' },
  EXPIRED: { label: 'Expired', variant: 'neutral' },

  // terminal
  CANCELLED: { label: 'Cancelled', variant: 'neutral' },
  VOID: { label: 'Void', variant: 'neutral' },
}

/**
 * Metadata for a status, tolerating one we don't know.
 *
 * The BE owns this enum and can add to it; a status we've never seen must degrade to a readable
 * label, not crash the row it appears in. Turning `PAYMENT_ON_HOLD` into "Payment on hold" is a
 * better failure than taking down the invoice list.
 */
function metaFor(status: InvoiceStatus): StatusMeta {
  return (
    STATUS_META[status] ?? {
      label: String(status).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()),
      variant: 'neutral',
    }
  )
}

/** The server's pretty-printed status when it sent one, else our own label. */
export function statusLabel(inv: { status: InvoiceStatus; displayStatus?: string | null }): string {
  return inv.displayStatus?.trim() || metaFor(inv.status).label
}

/*
 * `variant` stays on STATUS_META for now — it is the record of what colour each status USED to
 * get, and W3.T3.1's invoiceStatus map in lib/status_colors was derived from it. The exported
 * `statusVariant` helper is gone: StatusIndicator type="invoice" resolves colour now, so nothing
 * read it any more.
 */

/** Money still owed: raised or in-flight, not yet settled and not closed out. */
export const OUTSTANDING_STATUSES: InvoiceStatus[] = [
  'SUBMITTED',
  'ISSUED',
  'RECEIVED',
  'PENDING',
  'PROCESSING',
  'APPROVED',
  'VERIFIED',
  'PAYOUT_SCHEDULED',
  'PAYMENT_INITIATED',
  'PARTIALLY_PAID',
  'OVERDUE',
]

export const PAID_STATUSES: InvoiceStatus[] = ['PAID', 'PAYMENT_RECEIVED']

export const DRAFT_STATUSES: InvoiceStatus[] = ['DRAFT', 'READY_TO_RAISE']

/**
 * The recipient's pending-review queue: raised/received and not yet approved or rejected. Pushed to
 * the server as a multi-status filter (`InvoiceFilterInput.statuses`) so the "To approve" tab is a
 * real query, not a slice of the 200-row window that could truncate away older pending items.
 */
export const PENDING_REVIEW_STATUSES: InvoiceStatus[] = ['SUBMITTED', 'PENDING', 'PROCESSING', 'RECEIVED']

/** Closed out — no money will move and no action remains. */
export const TERMINAL_STATUSES: InvoiceStatus[] = ['PAID', 'CANCELLED', 'VOID']

// ── Transition matrix ──────────────────────────────────────────────

/**
 * A mirror of invoice-svc's `TRANSITIONS` table
 * (`apps/invoice-svc/src/modules/invoice/lifecycle/transition-matrix.ts`).
 *
 * This is a UX optimisation ONLY: it stops us offering a button whose sole outcome is an
 * `INV_ILLEGAL_STATUS_TRANSITION` round-trip. The server remains the sole authority on legality —
 * if this table drifts, the worst case is an action that looks available and is then refused,
 * never an illegal move that succeeds.
 *
 * Terminal states have no onward edge; refund/reopen are a later phase.
 */
export const TRANSITIONS: Record<InvoiceStatus, InvoiceStatus[]> = {
  DRAFT: ['SUBMITTED', 'ISSUED', 'READY_TO_RAISE', 'PENDING', 'VOID', 'CANCELLED'],
  SUBMITTED: ['APPROVED', 'REJECTED', 'PROCESSING', 'HOLD', 'RECALLED', 'CANCELLED'],
  READY_TO_RAISE: ['MANAGER_RAISED', 'ISSUED', 'CANCELLED', 'VOID'],
  MANAGER_RAISED: ['MANAGER_APPROVED', 'MANAGER_REJECTED', 'RECALLED'],
  MANAGER_APPROVED: ['ISSUED', 'READY_TO_RAISE'],
  MANAGER_REJECTED: ['DRAFT', 'RECALLED'],
  ISSUED: [
    'RECEIVED',
    'APPROVED',
    'REJECTED',
    'PROCESSING',
    'PARTIALLY_PAID',
    'PAID',
    'OVERDUE',
    'HOLD',
    'VOID',
    'CANCELLED',
    'RECALLED',
    'EXPIRED',
  ],
  RECEIVED: ['APPROVED', 'REJECTED', 'PROCESSING', 'HOLD'],
  PENDING: ['PROCESSING', 'APPROVED', 'REJECTED', 'HOLD', 'CANCELLED'],
  PROCESSING: ['APPROVED', 'REJECTED', 'VERIFIED', 'PAYMENT_FAILED', 'HOLD'],
  APPROVED: [
    'VERIFIED',
    'PAYOUT_SCHEDULED',
    'PAYMENT_INITIATED',
    'PARTIALLY_PAID',
    'PAID',
    'HOLD',
    'VOID',
  ],
  VERIFIED: ['PAYOUT_SCHEDULED', 'PAYMENT_INITIATED', 'APPROVED', 'HOLD'],
  REJECTED: ['DRAFT', 'RECALLED', 'CANCELLED', 'VOID'],
  OVERDUE: ['PARTIALLY_PAID', 'PAID', 'PAYMENT_INITIATED', 'EXPIRED', 'CANCELLED', 'VOID', 'HOLD'],
  PAYOUT_SCHEDULED: ['PAYMENT_INITIATED', 'PAYMENT_FAILED', 'HOLD'],
  PAYMENT_INITIATED: ['PAYMENT_RECEIVED', 'PAYMENT_FAILED', 'PARTIALLY_PAID', 'PAID'],
  PAYMENT_FAILED: ['PAYMENT_INITIATED', 'PAYOUT_SCHEDULED', 'HOLD', 'CANCELLED'],
  PAYMENT_RECEIVED: ['PAID', 'PARTIALLY_PAID'],
  PARTIALLY_PAID: ['PAID', 'OVERDUE', 'PAYMENT_INITIATED', 'HOLD'],
  RECALLED: ['DRAFT', 'SUBMITTED', 'CANCELLED'],
  EXPIRED: ['CANCELLED', 'VOID'],
  HOLD: [
    'SUBMITTED',
    'ISSUED',
    'RECEIVED',
    'PENDING',
    'PROCESSING',
    'APPROVED',
    'VERIFIED',
    'OVERDUE',
    'PARTIALLY_PAID',
    'PAYOUT_SCHEDULED',
    'PAYMENT_INITIATED',
    'PAYMENT_FAILED',
    'REJECTED',
    'CANCELLED',
    'VOID',
  ],
  PAID: [],
  CANCELLED: [],
  VOID: [],
}

export const isTransitionAllowed = (from: InvoiceStatus, to: InvoiceStatus): boolean =>
  TRANSITIONS[from]?.includes(to) ?? false

// ── Actions ────────────────────────────────────────────────────────

export interface InvoiceAction {
  /** The intent handed to `performInvoiceOperations`. */
  operation: InvoiceOperation
  /** The target status. The server's matrix still decides whether the edge is legal. */
  to: InvoiceStatus
  label: string
  /** Destructive actions render as outline/danger rather than the primary button. */
  tone?: 'default' | 'danger'
  /**
   * Money-movement. Renders DARK — the mutation behind it throws INV_PEER_NOT_ACTIVATED until the
   * wallet/payment peer is live, so shipping it as functional would be a lie.
   */
  dark?: boolean
  /** HOLD/UNHOLD need a hold type, so they open a dialog instead of firing directly. */
  needsHoldType?: boolean
  /**
   * REJECT needs a mandatory reason (invoice-svc throws INV_REJECTION_REASON_REQUIRED without one,
   * and the supplier is shown it), so it opens a dialog instead of firing directly.
   */
  needsReason?: boolean
}

/**
 * The action catalogue, keyed by intent. Deliberately smaller than the matrix: the matrix contains
 * every legal machine edge, most of which are system moves (PAYMENT_RECEIVED, EXPIRED, …) that no
 * human should trigger from a toolbar.
 */
const ACTIONS: InvoiceAction[] = [
  { operation: 'SUBMIT', to: 'SUBMITTED', label: 'Submit' },
  { operation: 'ISSUE', to: 'ISSUED', label: 'Issue' },
  { operation: 'APPROVE', to: 'APPROVED', label: 'Approve' },
  { operation: 'REJECT', to: 'REJECTED', label: 'Reject', tone: 'danger', needsReason: true },
  { operation: 'PAY', to: 'PAID', label: 'Mark as paid' },
  { operation: 'HOLD', to: 'HOLD', label: 'Place on hold', needsHoldType: true },
  { operation: 'RECALL', to: 'RECALLED', label: 'Recall' },
  { operation: 'CANCEL', to: 'CANCELLED', label: 'Cancel', tone: 'danger' },
  { operation: 'VOID', to: 'VOID', label: 'Void', tone: 'danger' },
]

/**
 * Who may even ask for each intent.
 *
 * UX only — invoice-svc authorizes every real call against the persona predicate and the acl
 * grants, so this narrows the menu to what makes sense for the role; it does not protect anything.
 * SA sees everything because platform ops legitimately reaches across the lifecycle.
 */
/**
 * The server approver set is CLIENT / AGENCY / ENTITY / PLATFORM_ADMIN. The FE persona union
 * doesn't model `ENTITY` as a first-class list persona (it has no dashboard/list config), but
 * `normalizePersona` passes an unknown server persona through unchanged — so an ENTITY-context
 * caller reaches this table with `persona === 'ENTITY'`. Widen only this gating table (not the core
 * `Persona` union, which would force ENTITY entries in every `Record<Persona, …>`) so ENTITY is
 * offered approve/reject. `SA` here is the FE alias for server `PLATFORM_ADMIN`.
 */
type ActionPersona = Persona | 'ENTITY'

const OPERATION_PERSONAS: Record<InvoiceOperation, ActionPersona[]> = {
  SUBMIT: ['VENDOR', 'AGENCY', 'SA'],
  ISSUE: ['VENDOR', 'AGENCY', 'SA'],
  APPROVE: ['CLIENT', 'AGENCY', 'ENTITY', 'SA'],
  REJECT: ['CLIENT', 'AGENCY', 'ENTITY', 'SA'],
  PAY: ['CLIENT', 'AGENCY', 'SA'],
  HOLD: ['CLIENT', 'AGENCY', 'SA'],
  UNHOLD: ['CLIENT', 'AGENCY', 'SA'],
  RECALL: ['VENDOR', 'AGENCY', 'SA'],
  CANCEL: ['VENDOR', 'AGENCY', 'SA'],
  VOID: ['SA'],
  MOVE: ['SA'],
}

/**
 * The UI permission-key that gates each lifecycle intent. Every action control MUST be wrapped in
 * `<Can k={invoiceActionAclKey(op)} route={AclRoute.INVOICE}>` (or filtered on `checkPermission`) so
 * it renders only for a persona the BE would actually let perform it. Fail-closed and UX-only — the
 * server re-authorizes every real call.
 *
 * Mapping rationale (live dev ui_permission_map):
 *  - raise-side lifecycle (SUBMIT/ISSUE/RECALL/CANCEL) → `invoices:raise` (SA/AGENCY/VENDOR)
 *  - approver verbs (APPROVE/REJECT) → `invoices:approve` (SA/AGENCY/CLIENT)
 *  - holds (HOLD/UNHOLD) → `invoices:hold` (SA/AGENCY/CLIENT)
 *  - PAY → `invoices:pay`, VOID/MOVE → `invoices:void` (⚠️ both need CSV rows added BE-side)
 */
export function invoiceActionAclKey(operation: InvoiceOperation): string {
  switch (operation) {
    case 'SUBMIT':
    case 'ISSUE':
    case 'RECALL':
    case 'CANCEL':
      return InvoiceAclKeys.raise
    case 'APPROVE':
    case 'REJECT':
      return InvoiceAclKeys.approve
    case 'HOLD':
    case 'UNHOLD':
      return InvoiceAclKeys.hold
    case 'PAY':
      return InvoiceAclKeys.pay
    case 'VOID':
    case 'MOVE':
      return InvoiceAclKeys.void
  }
}

/**
 * The actions to offer for an invoice: legal by the matrix, sensible for the persona, and — for
 * HOLD — inverted into UNHOLD when the invoice is already held.
 *
 * WORKER gets none: payslips are read-only on this surface.
 */
export function availableActions(status: InvoiceStatus, persona: Persona | undefined): InvoiceAction[] {
  if (!persona || persona === 'WORKER') return []

  // On hold, the only sensible verb is releasing it. The matrix allows HOLD → many states; the
  // release itself is an UNHOLD intent, and the target is chosen in the dialog.
  if (status === 'HOLD') {
    return OPERATION_PERSONAS.UNHOLD.includes(persona)
      ? [{ operation: 'UNHOLD', to: 'APPROVED', label: 'Release hold', needsHoldType: true }]
      : []
  }

  return ACTIONS.filter(
    (a) => isTransitionAllowed(status, a.to) && OPERATION_PERSONAS[a.operation].includes(persona),
  )
}

// ── DARK operations ────────────────────────────────────────────────

/**
 * Mutations that exist on the schema but throw `INV_PEER_NOT_ACTIVATED` today, because the peer
 * service behind each (wallet/payment, activation-send, payroll) is not live. Every control that
 * drives one of these must render disabled with a "not available yet" affordance — never as a
 * working button that fails on click.
 */
export const INVOICE_DARK_OPERATIONS = [
  'triggerInvoicesBulkPayout',
  'disburseInvoice',
  'removeChildInvoiceDisbursementInvoice',
  'sendActivationInvoice',
  'autoRaisePayrollInvoices',
] as const

export const DARK_ACTION_HINT = 'Payments are not switched on for this workspace yet.'

// ── Invoice types ──────────────────────────────────────────────────

/** Reserved on the BE but not active — never offer these when raising. */
const RESERVED_INVOICE_TYPES: InvoiceType[] = ['EOR', 'EMPLOYEE', 'CONTRACTOR']

export const RAISEABLE_INVOICE_TYPES: InvoiceType[] = (
  [
    'VENDOR',
    'PLATFORM_FEE',
    'CREDIT_NOTE',
    'DEBIT_NOTE',
    'REIMBURSEMENT',
    'EXPENSE',
    'ADVANCE',
    'MILESTONE',
    'PRORATION',
    'ADJUSTMENT',
    'CONSOLIDATED',
    'MISCELLANEOUS',
  ] as InvoiceType[]
).filter((t) => !RESERVED_INVOICE_TYPES.includes(t))

const TYPE_LABELS: Partial<Record<InvoiceType, string>> = {
  VENDOR: 'Vendor',
  PLATFORM_FEE: 'Platform fee',
  CREDIT_NOTE: 'Credit note',
  DEBIT_NOTE: 'Debit note',
  REIMBURSEMENT: 'Reimbursement',
  EXPENSE: 'Expense',
  ADVANCE: 'Advance',
  MILESTONE: 'Milestone',
  PRORATION: 'Proration',
  ADJUSTMENT: 'Adjustment',
  CONSOLIDATED: 'Consolidated',
  MISCELLANEOUS: 'Miscellaneous',
}

export const invoiceTypeLabel = (type: InvoiceType): string =>
  TYPE_LABELS[type] ?? type.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())

// ── Provenance ─────────────────────────────────────────────────────

/**
 * True when someone raised this invoice on the supplier's behalf — an agency or the platform acting
 * for a vendor. When the raiser IS the supplier it's a direct invoice and there is nothing to say,
 * which is why this is a conditional badge and not a column.
 *
 * A cross-tenant raise (`raisedByTenant` set) is never "on behalf": invoice-svc validates the
 * supplier inside the raising tenant, so raiser and supplier are the same organisation by
 * construction. Their party ids still differ there — `raisedByParty` is the raiser's membership,
 * `supplierPartyId` the issuing legal entity's party — so comparing ids would badge every
 * direct raise. Only an intra-tenant row falls back to the party comparison.
 */
export function isRaisedOnBehalf(
  inv: {
    raisedByParty?: string | null
    supplierPartyId?: string | null
    raisedByTenant?: string | null
  },
): boolean {
  if (inv.raisedByTenant || !inv.supplierPartyId) return false
  return Boolean(inv.raisedByParty) && inv.raisedByParty !== inv.supplierPartyId
}

// ── Money ──────────────────────────────────────────────────────────

/**
 * The figure to show for an invoice. `billingAmount` is what the counterparty is actually billed;
 * `amount` is the pre-billing figure. Prefer billing, fall back, and treat null as zero rather than
 * rendering "NaN" — both are nullable on the wire.
 */
export const invoiceAmount = (inv: Pick<InvoiceLike, 'amount' | 'billingAmount'>): number =>
  inv.billingAmount ?? inv.amount ?? 0

/**
 * D4 — how to refer to an invoice that may not have a number yet.
 *
 * `invoiceNumber` is present-and-null (never absent, never undefined): the BE field is
 * `nullable: true` and both mappers coerce `?? null`. BE is moving number assignment from
 * draft-save to raise, and is GRANDFATHERING already-numbered drafts — so the list will carry a
 * MIXED population, some drafts numbered and some not, indefinitely.
 *
 * Every call site already fell back to an id fragment, so nothing rendered empty. The gap was that
 * the fallback is visually indistinguishable from a real number: a monospace uppercase token in a
 * column headed "Invoice". A user could not tell "not numbered yet" from "numbered differently".
 *
 * So the fallback is still a usable handle — it identifies the row, and dialogs and aria-labels
 * need one — but it now says which of the two it is, and the copy matches what the create form
 * already promises ("Number assigned on raise").
 */
export function invoiceRef(invoice: { invoiceNumber?: string | null; id: string }): {
  text: string
  assigned: boolean
  /** Why there is no number — null when there is one, so callers can render nothing. */
  hint: string | null
} {
  const number = invoice.invoiceNumber?.trim()
  if (number) return { text: number, assigned: true, hint: null }
  return {
    text: invoice.id.slice(0, 8).toUpperCase(),
    assigned: false,
    hint: 'Number assigned on raise',
  }
}

/**
 * The locale every money figure is formatted in, pinned deliberately.
 *
 * Matches the locale this repo already pinned for dates rather than inventing a second convention.
 * Changing it changes what every user sees, so it is a product decision, not a refactor.
 */
const MONEY_LOCALE = 'en-GB'

export function formatMoney(amount: number | null | undefined, currency: string): string {
  // 'en-GB' explicitly, not `undefined` — the same fix this repo already made for dates (see
  // formatDueDate below, and ~15 other formatters). `undefined` resolves to the HOST's ambient
  // ICU locale, so the same amount renders differently on a dev machine, in CI, and in each
  // user's browser. Measured, not assumed: USD renders "$0.00" under this machine's en-IN and
  // under en-US, but "US$0.00" under en-GB — three spellings of one value.
  //
  // It also made money assertions untrustworthy: a D3 test that asserted against a hardcoded
  // "US$" passed VACUOUSLY here because en-IN never produces that string, and stayed green when
  // the defect it guarded was reinstated. Pinning the locale is what makes such an assertion
  // mean anything.
  //
  // Rebase (2026-09-22): dev consolidated this onto lib/formatters' `formatCurrency`, whose
  // `locale` option defaults to `undefined` — i.e. the ambient locale this pin exists to avoid.
  // Keeping the shared formatter AND the pin: one implementation, still deterministic.
  return formatCurrency(amount ?? 0, currency, { locale: MONEY_LOCALE })
}

export interface CurrencyTotal {
  currency: string
  total: number
}

/** Sum per currency. Amounts in different currencies are never added together. */
export function sumByCurrency(invoices: InvoiceLike[], statuses?: InvoiceStatus[]): CurrencyTotal[] {
  const set = statuses ? new Set(statuses) : null
  const byCurrency = new Map<string, number>()
  for (const inv of invoices) {
    if (set && !set.has(inv.status)) continue
    byCurrency.set(inv.currency, (byCurrency.get(inv.currency) ?? 0) + invoiceAmount(inv))
  }
  return [...byCurrency.entries()]
    .map(([currency, total]) => ({ currency, total }))
    .sort((a, b) => b.total - a.total)
}

/**
 * Headline figure for a KPI card: the largest currency group, with a "+N more" suffix when the set
 * spans several currencies. Honest about the fact that they are not summable.
 */
export function headlineTotal(
  groups: CurrencyTotal[],
  /**
   * D3 — the currency actually in play in the surrounding window, used ONLY for the zero state.
   * Omit it when the window itself is empty: there is then no currency to claim.
   */
  windowCurrency?: string,
): { primary: string; more: number } {
  // D3 (live defect, browser repro on app-dev) — this previously returned formatMoney(0, 'USD'),
  // hardcoded. An empty status group therefore ASSERTED a currency that was not in play: a
  // GBP-only tenant saw "OVERDUE US$0.00" beside "Outstanding £2,103.00", and a context whose KPI
  // status sets matched no rows rendered EVERY card in US$ while its only invoice was in GBP.
  // A zero is a fact; the currency of a zero that has no rows behind it is not, so we either take
  // it from the surrounding window or decline to state it at all.
  if (groups.length === 0) {
    return { primary: windowCurrency ? formatMoney(0, windowCurrency) : ZERO_NO_CURRENCY, more: 0 }
  }
  return { primary: formatMoney(groups[0].total, groups[0].currency), more: groups.length - 1 }
}

/** Rendered when a zero has no rows and no window currency behind it — never a guessed symbol. */
const ZERO_NO_CURRENCY = '0.00'

/**
 * D2 (live defect) — the KPI card's secondary figure.
 *
 * It counts only the invoices in THIS KPI's status set, which is the same set the card's money
 * figure is summed over. It previously rendered as `${count} total`, which read as a count of the
 * whole list: "INVOICES 0 total" sat above one listed invoice, and "2 total" above eight.
 *
 * The count was right and the word was wrong. Recounting to match the table would have made the
 * count disagree with the money on the same card — a worse inconsistency than the reported one —
 * so the label names what is actually counted instead.
 */
export function kpiCountLabel(count: number): string {
  return `from ${count} invoice${count === 1 ? '' : 's'}`
}

// ── F1: copy-from-previous-invoice (EW-CIF-T06) ────────────────────

/**
 * A FRESH descriptive reference generated from the copied service + the CURRENT period, never the
 * source invoice's own reference (PRD AC-4b — the mockup derives, never copies:
 * `x.label + ' · Sep 2026'`). 'en-GB' explicitly, not `undefined` — this file's own `formatMoney`
 * documents why (ambient ICU locale differs between dev machine and CI).
 */
export function currentPeriodLabel(): string {
  return new Date().toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

export function generatedReference(service: string): string {
  const trimmed = service.trim()
  const label = trimmed ? trimmed[0].toUpperCase() + trimmed.slice(1) : 'Invoice'
  return `${label} · ${currentPeriodLabel()}`
}

export interface AppliedLineItem {
  id: number
  description: string
  qty: string
  unitPrice: string
  taxRate: string
  categoryKey?: string
  taxReviewed: boolean
}

export interface PreviousInvoiceApplyResult {
  /** '' means "could not resolve — leave Bill To unset" (AC-2c); paired with `billToMessage`. */
  receiverTenant: string
  billToMessage: string | null
  /** '' means "could not resolve — leave Bill From unset" (AC-2d); paired with `billFromMessage`. */
  issuingLegalEntityId: string
  billFromMessage: string | null
  currency: string
  items: AppliedLineItem[]
  referenceId: string
  draftSource: { title: string; text: string }
}

/**
 * The pure computation behind F1's "Apply" (PRD AC-2/AC-2a-2d/AC-3/AC-4/AC-4a) — no React, no
 * setState, so every branch (resolved / not-found, empty line items, blank service) is a plain
 * function call to test. `CreateInvoicePage.tsx`'s `applyPreviousInvoice` is the thin wiring layer
 * that feeds this result into the page's own state setters.
 *
 * Three rules enforced here, not negotiable: every copied line arrives with `taxReviewed: false`
 * (Rule 1 — never the source's own review state); the source's invoice number, dates and
 * `supplierInvoiceNumber` are simply never read (Rule 2 — per-invoice identity); and no custom
 * field appears anywhere in this result (Rule 3 — `CustomFieldInputs` starts empty on every copy).
 */
export function resolvePreviousInvoiceApply(
  source: InvoiceDetailFragment,
  lists: {
    clients: { tenantId: string; partyId: string | null; displayName: string }[]
    entities: { id: string }[]
  },
): PreviousInvoiceApplyResult {
  const ref = invoiceRef(source)

  // AC-2b/AC-2c — resolved through the SAME vendor-mode-aware list the rest of the page uses
  // (`lists.clients`), matched by partyId — never a name guess. Not found is expected, not
  // exceptional (an ended relationship, or a party the caller can no longer act for).
  const matchedClient = lists.clients.find((c) => c.partyId === source.customerPartyId) ?? null
  const receiverTenant = matchedClient?.tenantId ?? ''
  const billToMessage = matchedClient
    ? null
    : `The client on ${ref.text} is no longer in your client list — choose one.`

  // AC-2d — Bill From gets the identical treatment.
  const matchedEntity = lists.entities.find((e) => e.id === source.issuingLegalEntityId) ?? null
  const issuingLegalEntityId = matchedEntity?.id ?? ''
  const billFromMessage = matchedEntity
    ? null
    : `The issuing entity on ${ref.text} could not be resolved — choose one.`

  // Line items. The source line's `taxRate` IS copied (human ruling 2026-09-18, option 3) — the
  // earlier non-copy was an implementation extrapolation, not a PRD rule: Rule 1 forbids copying the
  // REVIEW STATE, and conflating the rate with it under-charged silently. INV-000003's line is 20%
  // on £2,000 with no categoryKey, so nothing re-suggested a rate and nothing flagged the line:
  // the copy arrived at 0% and could be raised £400 short. Every copied line still arrives
  // `taxReviewed: false`, exactly as a rule-suggested rate does.
  //
  // A copied rate need not be one of the control's standard choices (`LINE_TAX_RATES`); the Tax
  // control is passed `unlistedValueLabel`, so it displays whatever the line actually carries
  // rather than a blank trigger.
  const items: AppliedLineItem[] =
    source.lineItems.length > 0
      ? source.lineItems.map((li, i) => ({
          id: i + 1,
          description: li.description ?? '',
          qty: li.quantity != null ? String(li.quantity) : '1',
          unitPrice: li.unitRate != null ? String(li.unitRate) : '',
          taxRate: li.taxRate != null ? String(li.taxRate) : '',
          categoryKey: li.categoryKey ?? undefined,
          taxReviewed: false,
        }))
      : [{ id: 1, description: '', qty: '1', unitPrice: '', taxRate: '', taxReviewed: false }]

  // AC-4a — the draft-source banner. `{client}` uses the SAME resolution as AC-2c: present only
  // when the client actually resolved (including 'Unnamed organisation' when that's what
  // `displayName` resolved to — the shared resolver's own fallback, not invented here), omitted
  // entirely — never a placeholder or a dangling separator — in the former-client case.
  const draftSource = {
    title: matchedClient ? `Copied from ${ref.text} · ${matchedClient.displayName}` : `Copied from ${ref.text}`,
    text: 'Client-specific commercial values were copied. Current invoice date is retained; tax review must be completed again for this new invoice.',
  }

  return {
    receiverTenant,
    billToMessage,
    issuingLegalEntityId,
    billFromMessage,
    currency: source.currency,
    items,
    referenceId: generatedReference(source.service ?? ''),
    draftSource,
  }
}
